import type { User } from '@/@types/user'
import { getFollowerListQueryOptions, getFollowingListQueryOptions } from '@/utils/fetchers/user'
import { useCurrentUser } from '@/utils/hooks/useUser'
import { getThirdPersonPronoun } from '@/utils/thirdPersonPronoun'
import { Modal } from '@mantine/core'
import { usePrefetchInfiniteQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { isEmpty } from 'lodash-es'
import { motion } from 'motion/react'
import { type HTMLAttributes, useState } from 'react'
import Avatar from './Avatar'
import FollowToggleButton from './FollowToggleButton'
import HoverableText from './HoverableText'
import LoadableUserList from './LoadableUserList'
import ScreenName from './ScreenName'
import styles from './UserProfile.module.css'
import UserProfileTag from './UserProfileTag'

export interface UserProfileProps extends HTMLAttributes<HTMLDivElement> {
	data: User
}

export default function UserProfile({ data, className, ...props }: UserProfileProps) {
	const [isFollowingListModalShow, setIsFollowingListModalShow] = useState(false)
	const [isFollowerListModalShow, setIsFollowerListModalShow] = useState(false)

	const currentUser = useCurrentUser()
	const isCurrentUser = currentUser.id === data.id

	usePrefetchInfiniteQuery(getFollowingListQueryOptions(data.username))
	usePrefetchInfiniteQuery(getFollowerListQueryOptions(data.username))

	if (!data) {
		return null
	}

	const {
		backgroundImage,
		backgroundMaskColor,
		avatarImage,
		zodiac,
		screenName,
		industry,
		bio,
		statsCount,
		isSponsor,
		decorations,
		gender,
		city,
		profileTags = [],
	} = data
	const hasBackgroundImage = !!backgroundImage?.picUrl

	return (
		<section className={clsx(styles.section, className)} {...props}>
			{/* background image */}
			<div className={styles.backgroundImage}>
				<motion.img
					initial={{
						opacity: 0,
					}}
					animate={{
						opacity: 1,
					}}
					loading="lazy"
					src={backgroundImage?.picUrl ?? avatarImage.picUrl}
					className={clsx(
						styles.backgroundImageImg,
						!hasBackgroundImage && styles.backgroundImageImgBlur
					)}
				/>
			</div>

			<div className={styles.content}>
				<section>
					<Avatar data={data} avatarKey="picUrl" className={styles.avatar} />

					<section className={styles.infoSection}>
						<div className={styles.nameRow}>
							<ScreenName data={data} className={styles.screenName} />
							<FollowToggleButton data={data} />
						</div>

						<section className={styles.statsSection}>
							<div className={styles.stat}>
								<HoverableText
									className={styles.statCount}
									onClick={() => {
										setIsFollowingListModalShow(true)
									}}
									aria-label="关注数"
								>
									{statsCount.followingCount}
								</HoverableText>
								<span className={styles.statLabel}>关注</span>
							</div>
							<div className={styles.stat}>
								<HoverableText
									className={styles.statCount}
									onClick={() => {
										setIsFollowerListModalShow(true)
									}}
									aria-label="被关注数"
								>
									{statsCount.followedCount}
								</HoverableText>
								<span className={styles.statLabel}>被关注</span>
							</div>
							<div className={styles.stat}>
								<HoverableText className={styles.statCount}>
									{statsCount.respectedCount}
								</HoverableText>
								<span className={styles.statLabel}>夸夸</span>
							</div>
						</section>

						{/* member */}
						{isSponsor && decorations?.sponsor?.picUrl && (
							<section className={styles.sponsorSection}>
								<img
									width={24}
									height={24}
									loading="lazy"
									referrerPolicy="no-referrer"
									src={decorations.sponsor?.picUrl}
									className={styles.sponsorImage}
								/>
								<span>Jike Yellow</span>
							</section>
						)}

						{!isEmpty(bio) && <section className={styles.bio}>{bio}</section>}

						{!isEmpty(profileTags) && (
							<section className={styles.tagsSection}>
								{profileTags.map((item) => (
									<UserProfileTag data={item} key={JSON.stringify(item)} />
								))}
							</section>
						)}
					</section>
				</section>
			</div>

			<Modal
				title={isCurrentUser ? '我关注的人' : `${getThirdPersonPronoun(data.gender)}关注的人`}
				opened={isFollowingListModalShow}
				onClose={() => void setIsFollowingListModalShow(false)}
			>
				<LoadableUserList username={data.username} type="following" />
			</Modal>

			<Modal
				title={isCurrentUser ? '关注我的人' : `关注${getThirdPersonPronoun(data.gender)}的人`}
				opened={isFollowerListModalShow}
				onClose={() => void setIsFollowerListModalShow(false)}
			>
				<LoadableUserList
					username={data.username}
					type="follower"
					className={styles.loadableUserList}
				/>
			</Modal>
		</section>
	)
}
